import { ChangeEvent, Dispatch, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import InfoIcon from '@mui/icons-material/Info';
import { selectCurrentUser, UserAccessLevels } from '../../../redux/user';
import { getTeamMembers } from '../../../redux/customer';
import { getChildTeamMembers } from '../../../redux/childCustomer';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import CloseModalButton from '../../../components/CloseModalButton';
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../components/Dialog.styled';
import { AppUser, OpenTeamMemberModal, TeamRoles } from './types';
import { addError, addSuccess } from '../../../redux/alerts';
import { Groups, JobTitle, TeamMember } from '../../../types';
import { createUser, updateUser, updateUserGroups } from '../../../services/teamMemberService';
import { getGroups, getJobTitle, getRole, validateName, validateEmail, validateJobTitle } from './utils/helpers';
import { logActivity } from '../../../helpers/logging';
import useIsCorpView from '../../../hooks/useIsCorpView';

type TeamMemberModal = {
  customerNumber: string;
  openModal: OpenTeamMemberModal;
  setOpenModal: Dispatch<SetStateAction<OpenTeamMemberModal>>;
  groups: Partial<Groups>[] | null;
  jobTitles: JobTitle[];
  teamMembers: TeamMember[];
};

const TeamMemberModal = (props: TeamMemberModal) => {
  const { customerNumber, openModal, setOpenModal, groups, jobTitles, teamMembers } = props;

  const validationInitialState = {
    firstName: { isValid: true, message: '' },
    lastName: { isValid: true, message: '' },
    role: { isValid: true, message: '' },
    email: { isValid: true, message: '' },
    jobTitle: { isValid: true, message: '' },
    otherJobTitle: { isValid: true, message: '' }
  };

  const { teamMember } = openModal;

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);
  const isCorporateView = useIsCorpView()

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<keyof typeof TeamRoles | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Partial<Groups>[]>([]);
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState<JobTitle | null>(null);
  const [otherJobTitle, setOtherJobTitle] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [memberActive, setMemberActive] = useState(true);
  const [validations, setValidations] = useState(validationInitialState);
  const [notifications, setNotifications] = useState({ email_bio_passed: true, email_bio_failed: true, email_waterline_passed: true, email_waterline_failed: true, is_notification_team_member_editable: false });

  useEffect(() => {
    if (teamMember) {
      const { title, otherTitle } = getJobTitle(teamMember, jobTitles);
      const teamMemberGroups = getGroups(teamMember);

      if (teamMember.firstname) setFirstName(teamMember.firstname);
      if (teamMember.middlename) setMiddleName(teamMember.middlename);
      if (teamMember.lastname) setLastName(teamMember.lastname);
      if (teamMemberGroups.length) setSelectedGroups(teamMemberGroups);
      if (title) setJobTitle({ job_title: title });
      if (otherTitle) setOtherJobTitle(otherTitle);

      setNotifications({
        email_bio_passed: teamMember.email_bio_passed,
        email_bio_failed: teamMember.email_bio_failed,
        email_waterline_passed: teamMember.email_waterline_passed,
        email_waterline_failed: teamMember.email_waterline_failed,
        is_notification_team_member_editable: teamMember.is_notification_team_member_editable
      });
      setEmail(teamMember.email);
      setRole(getRole(teamMember.access_level as TeamRoles));
      setMemberActive(teamMember.account_active);
    }
  }, [teamMember]);

  useEffect(() => {
    if (!teamMembers.length) {
      setRole('Owner');
    }
  }, [teamMembers]);

  const roles = useMemo(() => {
    const keys = Object.keys(TeamRoles);

    return role === 'Owner' ? keys : keys.filter((role) => role !== 'Owner');
  }, [role]);

  const trimmed = useMemo(() => {
    return {
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      otherJobTitle: otherJobTitle.trim()
    };
  }, [firstName, middleName, lastName, email, otherJobTitle]);

  const position = useMemo(() => {
    return trimmed.otherJobTitle || jobTitle?.job_title;
  }, [otherJobTitle, jobTitle]);

  const hasRequiredFields =
    Boolean(customerNumber) &&
    Boolean(trimmed.firstName) &&
    Boolean(trimmed.lastName) &&
    Boolean(role) &&
    Boolean(trimmed.email) &&
    Boolean(position);

  const disableSave = !hasRequiredFields || isUpdating;

  const isOwner = TeamRoles[role as keyof typeof TeamRoles] === TeamRoles.Owner;

  const handleClose = () => setOpenModal({ open: false, teamMember: null });

  const handleSave = async () => {
    if (hasRequiredFields && role && position) {
      try {
        setIsUpdating(true);

        const accessLevel = TeamRoles[role];
        const activeAccount = teamMember ? memberActive : true;

        const user: AppUser = {
          customer_number: customerNumber,
          firstname: trimmed.firstName,
          middlename: trimmed.middleName ?? null,
          lastname: trimmed.lastName,
          email: trimmed.email,
          access_level: accessLevel,
          account_active: activeAccount,
          position: position,
          email_bio_passed: notifications.email_bio_passed,
          email_bio_failed: notifications.email_bio_failed,
          email_bio_child_failed: teamMember?.email_bio_child_failed,
          email_waterline_passed: notifications.email_waterline_passed,
          email_waterline_failed: notifications.email_waterline_failed,
          is_notification_team_member_editable: notifications.is_notification_team_member_editable,
          email_waterline_child_failed: teamMember?.email_waterline_child_failed,

        };

        const isExistingUser = teamMembers.find((teamMember) => teamMember.email === email);

        if (!teamMember && isExistingUser) {
          dispatch(addError('This user is already authorized for this practice'));
        } else {
          const groups = TeamRoles[role] === TeamRoles.Administrator ? selectedGroups : [];

          if (teamMember) {
            user.login_user_id = teamMember.login_user_id;
            user.groups = groups;
          }

          const upsertedUser = teamMember ? await updateUser(user, customerNumber) : await createUser(user, customerNumber);

          const oldValue = {
            access_level: teamMember?.access_level,
            accout_active: teamMember?.account_active,
            customer_number: teamMember?.customer_number,
            email: teamMember?.email,
            email_bio_failed: teamMember?.email_bio_failed,
            email_bio_passed: teamMember?.email_bio_passed,
            email_bio_child_failed: teamMember?.email_bio_child_failed,
            email_waterline_failed: teamMember?.email_waterline_failed,
            email_waterline_passed: teamMember?.email_waterline_passed,
            email_waterline_child_failed: teamMember?.email_waterline_child_failed,
            firstname: teamMember?.firstname,
            groups: teamMember?.groups,
            is_notification_team_member_editable: teamMember?.is_notification_team_member_editable,
            lastName: teamMember?.lastname,
            login_user_id: teamMember?.login_user_id,
            middlename: teamMember?.middlename,
            position: teamMember?.position
          }

          const log = {
            old_value: upsertedUser ? JSON.stringify(oldValue) : null,
            new_value: JSON.stringify(user)
          }

          const userId = upsertedUser.user_id.trim();

          await updateUserGroups(customerNumber, userId, groups, accessLevel);

          await logActivity(customerNumber, log, 'teamMember')

          dispatch(addSuccess(`Team member ${teamMember ? 'updated' : 'created'}`));

          isCorporateView ? dispatch(getChildTeamMembers(customerNumber)) : dispatch(getTeamMembers(customerNumber));
        }

        setIsUpdating(false);
        handleClose();
      } catch (err) {
        console.error(err);
        setIsUpdating(false);
        dispatch(addError('There was an issue with your request, please try again later'));
        handleClose();
      }
    }
  };

  const handleFirstName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.value;

    const cloned = structuredClone(validations);
    cloned.firstName = validateName(name);

    setValidations(cloned);
    setFirstName(name);
  };

  const handleMiddleName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMiddleName(event.target.value);
  };

  const handleLastName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.value;

    const cloned = structuredClone(validations);
    cloned.lastName = validateName(name);

    setValidations(cloned);
    setLastName(name);
  };

  const handleRole = (event: SyntheticEvent<Element, Event>, value: string | null) => {
    const cloned = structuredClone(validations);

    if (role && !value) cloned.role = { isValid: false, message: 'Role Required' };
    else cloned.role = { isValid: true, message: '' };

    if (value && (TeamRoles[value as keyof typeof TeamRoles] === TeamRoles.Administrator || TeamRoles[value as keyof typeof TeamRoles] === TeamRoles.Owner)) {
      setNotifications((state) => ({ ...state, is_notification_team_member_editable: true }));
    } else {
      setNotifications((state) => ({ ...state, is_notification_team_member_editable: false }));
    }

    setValidations(cloned);
    setRole(value as keyof typeof TeamRoles);
  };

  const handleGroup = (event: SyntheticEvent<Element, Event>, value: Partial<Groups>[]) => {
    setSelectedGroups(value);
  };

  const handleEmail = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const email = event.target.value;

    const cloned = structuredClone(validations);
    cloned.email = validateEmail(email);

    setValidations(cloned);
    setEmail(email);
  };

  const handleJobTitle = (event: SyntheticEvent<Element, Event>, value: JobTitle | null) => {
    const cloned = structuredClone(validations);

    if (jobTitle && !value) cloned.jobTitle = { isValid: false, message: 'Role Required' };
    else cloned.jobTitle = { isValid: true, message: '' };

    if (value?.job_title !== 'Other') {
      setOtherJobTitle('');
      cloned.otherJobTitle = { isValid: true, message: '' };
    }

    setValidations(cloned);
    setJobTitle(value);
  };

  const handleOtherJobTitle = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const jobTitle = event.target.value;

    const cloned = structuredClone(validations);
    cloned.otherJobTitle = validateJobTitle(jobTitle);

    setValidations(cloned);
    setOtherJobTitle(jobTitle);
  };

  const handleMemberActive = (event: SelectChangeEvent<string>) => {
    const isActive = event.target.value === 'Active';
    setMemberActive(isActive);

    if (!isActive) {
      setNotifications((state) => ({ ...state, email_bio_passed: false, email_bio_failed: false, email_waterline_passed: false, email_waterline_failed: false }))
    }
  };

  const handleNotifications = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setNotifications({ ...notifications, [name]: checked });
  };

  return (
    <>
      <Dialog open={openModal.open} onClose={handleClose} maxWidth="sm" fullWidth={true}>
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>
          <Box className="flex flex-align-center c-g-8">
            <PersonIcon fontSize="small" />
            <span>{openModal.teamMember ? 'Edit' : 'Add'} Team Member</span>
          </Box>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Box className="flex" sx={{ flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
            <TextField
              value={firstName}
              size="small"
              label="First Name (required)"
              onChange={handleFirstName}
              error={!validations.firstName.isValid}
              helperText={validations.firstName.message}
              fullWidth
            />
            <TextField value={middleName} size="small" label="Middle Name" onChange={handleMiddleName} fullWidth />
            <TextField
              value={lastName}
              size="small"
              label="Last Name (required)"
              onChange={handleLastName}
              error={!validations.lastName.isValid}
              helperText={validations.lastName.message}
              fullWidth
            />
            <FormControl fullWidth>
              <Autocomplete
                value={role}
                onChange={handleRole}
                options={roles}
                getOptionLabel={(option) => String(option)}
                size="small"
                disabled={role === 'Owner'}
                renderInput={(params) => (
                  <TextField {...params} label="Role (required)" error={!validations.role.isValid} helperText={validations.role.message} />
                )}
              />
            </FormControl>
            {groups && currentUser?.access_level === UserAccessLevels.Owner && role && TeamRoles[role] === TeamRoles.Administrator ? (
              <Autocomplete
                multiple
                value={selectedGroups}
                onChange={handleGroup}
                options={groups}
                isOptionEqualToValue={(option: Partial<Groups>, value: Partial<Groups>) => {
                  return option.group_id === value.group_id && option.name === value.name;
                }}
                disableCloseOnSelect
                getOptionLabel={(option) => String(option.name)}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={`${option.group_id}${option.name}`}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                size="small"
                renderInput={(params) => <TextField {...params} label="Groups" fullWidth />}
              />
            ) : null}
            <TextField
              value={email}
              size="small"
              label="Email (required)"
              onChange={handleEmail}
              error={!validations.email.isValid}
              helperText={validations.email.message}
              fullWidth
            />
            <Autocomplete
              value={jobTitle}
              onChange={handleJobTitle}
              options={jobTitles}
              isOptionEqualToValue={(option: JobTitle, value: JobTitle) => {
                return option.job_title.toLowerCase() === value.job_title.toLowerCase();
              }}
              getOptionLabel={(option) => String(option.job_title)}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Job Title (required)"
                  error={!validations.jobTitle.isValid}
                  helperText={validations.jobTitle.message}
                  fullWidth
                />
              )}
            />
            {jobTitle?.job_title === 'Other' ? (
              <TextField
                value={otherJobTitle}
                size="small"
                label="Other Job Title (required)"
                onChange={handleOtherJobTitle}
                error={!validations.otherJobTitle.isValid}
                helperText={validations.otherJobTitle.message}
                fullWidth
              />
            ) : null}
            {teamMember && (
              <FormControl fullWidth size="small">
                <InputLabel id="staff-member-status-label">Staff Member Active</InputLabel>
                <Select
                  labelId="staff-member-status-label"
                  id="staff-member-status-select"
                  value={memberActive ? 'Active' : 'Inactive'}
                  input={<OutlinedInput label="Staff Member Active" />}
                  onChange={handleMemberActive}
                >
                  {['Active', 'Inactive'].map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
              <span>Notification Preferences</span>
              <Tooltip title="Check the box below for the Team Member to receive notifications for each product">
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Box>
            <Box sx={{ fontSize: '14px', fontWeight: 'bold' }}>Biological Notification Preferences</Box>
            <FormGroup sx={{ margin: '0 8px', gap: '12px' }}>
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px'
                  }
                }}
                control={<Checkbox sx={{ mr: 1 }} checked={notifications.email_bio_passed} onChange={handleNotifications} name="email_bio_passed" />}
                label="Send team member email when office receives a pass test result"
              />
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px'
                  }
                }}
                control={<Checkbox sx={{ mr: 1 }} checked={notifications.email_bio_failed} onChange={handleNotifications} name="email_bio_failed" disabled={isOwner} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Send team member email when office receives a fail test result
                    <Tooltip title="Owners will always receive a failed test result notification. Best practice is to add two more Team Members who will receive fail test results.">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
              />
            </FormGroup>
            <Box sx={{ fontSize: '14px', fontWeight: 'bold' }}>Waterline Notification Preferences</Box>
            <FormGroup sx={{ margin: '0 8px', gap: '12px' }}>
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px'
                  }
                }}
                control={<Checkbox sx={{ mr: 1 }} checked={notifications.email_waterline_passed} onChange={handleNotifications} name="email_waterline_passed" />}
                label="Send team member email when office receives a pass test result"
              />
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px'
                  }
                }}
                control={<Checkbox sx={{ mr: 1 }} checked={notifications.email_waterline_failed} onChange={handleNotifications} name="email_waterline_failed" disabled={isOwner} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Send team member email when office receives a fail test result
                    <Tooltip title="Owners will always receive a failed test result notification. Best practice is to add two more Team Members who will receive fail test results.">
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
              />
            </FormGroup>
            {(role === null || TeamRoles[role] === TeamRoles.User) && (
              <>
                <Box sx={{ fontSize: '14px', fontWeight: 'bold' }}>Team Member Permissions</Box>
                <FormGroup sx={{ margin: '0 8px', gap: '12px' }}>
                  <FormControlLabel
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px'
                      }
                    }}
                    control={<Checkbox sx={{ mr: 1 }} checked={notifications.is_notification_team_member_editable} onChange={handleNotifications} name="is_notification_team_member_editable" />}
                    label="Allow team member to override the notification preferences in their settings page"
                  />
                </FormGroup>
              </>
            )}
            <Box sx={{ fontSize: '14px', fontWeight: 'bold' }}>Training Notification Preferences</Box>
            <FormGroup sx={{ margin: '0 8px' }}>
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px'
                  }
                }}
                control={<Checkbox sx={{ mr: 1 }} checked={true} onChange={handleNotifications} disabled />}
                label="*Any Team Member enrolled in Training will automatically receive training notifications"
              />
            </FormGroup>
          </Box>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={disableSave}>
            {isUpdating ? '...Saving' : 'Save'}
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  );
};

export default TeamMemberModal;
